import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { PhotoType } from '../services/serviceRequest.service';
import serviceRequestService from '../services/serviceRequest.service';
import api from '../services/api';

// Interface estendida para incluir a URL completa
interface ExtendedPhotoType extends PhotoType {
  fullUrl?: string;
}

interface PhotoGalleryProps {
  serviceRequestId: string;
  photos: PhotoType[];
  onPhotosUpdated: (photos: PhotoType[]) => void;
  readOnly?: boolean;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  serviceRequestId,
  photos,
  onPhotosUpdated,
  readOnly = false
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<ExtendedPhotoType | null>(null);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setNotification({
        message: 'Selecione um arquivo para upload',
        type: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Upload da foto
      const uploadResult = await serviceRequestService.uploadPhoto(selectedFile, description);
      console.log('Upload result:', uploadResult);
      
      // Adicionar a foto ao chamado técnico
      const result = await serviceRequestService.addPhotosToServiceRequest(serviceRequestId, [
        {
          url: uploadResult.url,
          description: uploadResult.description || description
        }
      ]);
      console.log('Add photos result:', result);
      
      // Buscar os dados atualizados do servidor
      const updatedServiceRequest = await serviceRequestService.getServiceRequestById(serviceRequestId);
      console.log('Updated service request:', updatedServiceRequest);
      
      // Atualizar a lista de fotos com os dados mais recentes
      if (updatedServiceRequest && 'photos' in updatedServiceRequest && updatedServiceRequest.photos && Array.isArray(updatedServiceRequest.photos)) {
        console.log('Atualizando fotos:', updatedServiceRequest.photos);
        onPhotosUpdated(updatedServiceRequest.photos as PhotoType[]);
      } else {
        console.warn('Fotos não encontradas na resposta do servidor');
      }
      
      // Fechar o diálogo e limpar os campos
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setDescription('');
      
      setNotification({
        message: 'Foto adicionada com sucesso',
        type: 'success'
      });
    } catch (error: any) {
      console.error('Erro ao fazer upload de foto:', error);
      setNotification({
        message: error.response?.data?.message || 'Erro ao fazer upload de foto',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para construir URLs completas para as imagens
  const getFullImageUrl = (url: string | undefined | null): string => {
    // Validar se a URL existe
    if (!url) {
      console.warn('URL da foto é undefined ou null');
      return ''; // Retornar string vazia se não houver URL
    }
    
    if (url.startsWith('http')) {
      return url; // Já é uma URL completa
    }
    
    // Obter a URL base da API
    const apiBaseUrl = api.defaults.baseURL?.replace('/api', '') || '';
    
    // Construir a URL completa
    return `${apiBaseUrl}${url}`;
  };
  
  // Processar as fotos para adicionar URLs completas
  const processedPhotos = useMemo<ExtendedPhotoType[]>(() => {
    return photos.map(photo => ({
      ...photo,
      fullUrl: getFullImageUrl(photo.url)
    }));
  }, [photos]);

  const handlePreview = (photo: ExtendedPhotoType) => {
    setSelectedPhoto(photo);
    setPreviewDialogOpen(true);
  };
  
  // Função para excluir uma foto
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<ExtendedPhotoType | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  
  const handleDeleteClick = (photo: ExtendedPhotoType) => {
    setPhotoToDelete(photo);
    setDeleteConfirmOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!photoToDelete || !photoToDelete._id) {
      setNotification({
        message: 'Erro: ID da foto não encontrado',
        type: 'error'
      });
      return;
    }
    
    try {
      setDeletingPhoto(true);
      
      // Chamar o serviço para excluir a foto
      await serviceRequestService.removePhotoFromServiceRequest(
        serviceRequestId,
        photoToDelete._id
      );
      
      // Buscar os dados atualizados do servidor
      const updatedServiceRequest = await serviceRequestService.getServiceRequestById(serviceRequestId);
      console.log('Updated service request após deletar:', updatedServiceRequest);
      
      // Atualizar a lista de fotos com os dados mais recentes
      if (updatedServiceRequest && 'photos' in updatedServiceRequest && updatedServiceRequest.photos && Array.isArray(updatedServiceRequest.photos)) {
        console.log('Atualizando fotos após deletar:', updatedServiceRequest.photos);
        onPhotosUpdated(updatedServiceRequest.photos as PhotoType[]);
      } else {
        console.warn('Fotos não encontradas na resposta do servidor após deletar');
        // Atualizar com array vazio se não houver fotos
        onPhotosUpdated([]);
      }
      
      setNotification({
        message: 'Foto removida com sucesso',
        type: 'success'
      });
    } catch (error: any) {
      console.error('Erro ao excluir foto:', error);
      setNotification({
        message: error.response?.data?.message || 'Erro ao excluir foto',
        type: 'error'
      });
    } finally {
      setDeletingPhoto(false);
      setDeleteConfirmOpen(false);
      setPhotoToDelete(null);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Fotos</Typography>
        {!readOnly && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{ display: 'block' }} // Garantir que o botão seja exibido
          >
            Adicionar Foto
          </Button>
        )}
      </Box>

      {photos.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Nenhuma foto disponível
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {processedPhotos.map((photo, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={photo._id || `photo-${index}`}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="140"
                  image={photo.fullUrl || photo.url}
                  alt={photo.description || `Foto ${index + 1}`}
                  sx={{ objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => handlePreview(photo)}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {photo.description || `Foto ${index + 1}`}
                  </Typography>
                  {photo.uploadDate && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(photo.uploadDate).toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <IconButton size="small" onClick={() => handlePreview(photo)}>
                    <ZoomInIcon />
                  </IconButton>
                  {!readOnly && (
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleDeleteClick(photo)}
                      aria-label="excluir foto"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Diálogo de upload de foto */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Adicionar Foto
          <IconButton
            aria-label="close"
            onClick={() => setUploadDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="photo-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="photo-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 2 }}
              >
                Selecionar Arquivo
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Arquivo selecionado: {selectedFile.name}
              </Typography>
            )}
            <TextField
              label="Descrição"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleUpload}
              disabled={loading || !selectedFile}
            >
              {loading ? <CircularProgress size={24} /> : 'Upload'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Diálogo de visualização de foto */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPhoto?.description || 'Visualização de Foto'}
          <IconButton
            aria-label="close"
            onClick={() => setPreviewDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedPhoto && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img
                src={selectedPhoto.fullUrl || getFullImageUrl(selectedPhoto.url)}
                alt={selectedPhoto.description || 'Foto'}
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
              />
              {selectedPhoto.description && (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {selectedPhoto.description}
                </Typography>
              )}
              {selectedPhoto.uploadDate && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Adicionada em: {new Date(selectedPhoto.uploadDate).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deletingPhoto && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Tem certeza que deseja excluir esta foto?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            disabled={deletingPhoto}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deletingPhoto}
            startIcon={deletingPhoto ? <CircularProgress size={20} /> : null}
          >
            {deletingPhoto ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificação */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.type || 'info'}
          sx={{ width: '100%' }}
        >
          {notification?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PhotoGallery;
